import { defineStore } from "pinia";
import { DEFAULT_SITE_LOCALE, resolveSiteDefaultLocale } from "~/constants/locale";

function translateMenuItem(item) {
  const title = item.titleKey ? useTranslateStore(item.titleKey) : item.title;
  const result = {
    ...item,
    title: item.titleBold ? `<b>${title}</b>` : title,
  };
  if (item.descriptionKey) result.description = useTranslateStore(item.descriptionKey);
  if (Array.isArray(item.items)) result.items = item.items.map(translateMenuItem);
  delete result.titleKey;
  delete result.titleBold;
  delete result.descriptionKey;
  return result;
}

// main is the name of the store. It is unique across your application
// and will appear in devtools
export const useGlobalStore = defineStore("global", {
  // a function that returns a fresh state
  state: () => ({
    globalData: {},
    clinicMapText: "",
    locale:
      useCookie("preferredCountry").value ?? resolveSiteDefaultLocale(useRuntimeConfig().public.siteDefaultLocale),
    localeDetail: {},
    locales: {},
    langCode: "en",
    defaultLocale: resolveSiteDefaultLocale(useRuntimeConfig().public.siteDefaultLocale),
    defaultSpainishLocale: "es-es",
    testMenu: [
      {
        titleKey: "mainmenu.testCategory.sexualHealth.title",
        descriptionKey: "mainmenu.testCategory.sexualHealth.introText",
        href: "/tests/sexual-health",
        showAustralia: true,
        items: [
          {
            titleKey: "mainmenu.overview",
            titleBold: true,
            hasChild: false,
            href: "/tests/sexual-health",
          },
        ],
      },
      {
        titleKey: "mainmenu.testCategory.healthAndWellness.title",
        descriptionKey: "mainmenu.testCategory.healthAndWellness.introText",
        href: "/tests/health-and-wellness",
        showAustralia: false,
        items: [
          {
            titleKey: "mainmenu.overview",
            titleBold: true,
            hasChild: false,
            href: "/tests/health-and-wellness",
          },
        ],
      },
    ],
    testHealthSubMenu: [],
    helpSubMenu: {},
    helpHealthSubMenu: [],
    helpSubMenuOverview: [
      {
        titleKey: "mainmenu.overview",
        titleBold: true,
        href: "/help-and-advice/sexual-health-and-stis",
      },
    ],
    serviceTypePageMenu: [],
    faqTopics: {},
    countrySwitcherVisibility: false,
    landbotChat: "",
    isHomeTestEnabled: false,
  }),
  // optional getters
  getters: {
    // get site image
    siteSettings: (state) => state.globalData,
    siteDefaultLocale: (state) => state.defaultLocale,
    siteLogo: (state) => state.globalData.logo,
    siteCountry: (state) => state.globalData.site.country_name,
    siteCountryCode: (state) => state.globalData.site.country_code,
    siteMapConfigLatLng: (state) => ({
      latitude: Number(state.globalData.map_configuration.default_startup_lat),
      longitude: Number(state.globalData.map_configuration.default_startup_lon),
      range: Number(state.globalData.map_configuration.default_startup_range),
    }),
    siteClusterSettings: (state) => ({
      showCluster: state.globalData.marker_clustering,
      maxClusterZoom: state.globalData.maximum_clustering_zoom,
    }),
    siteSearchZoom: (state) => Number(state.globalData.map_configuration.search_zoom),
    siteDefaultZoom: (state) => Number(state.globalData.map_configuration.default_startup_zoom),
    siteDefaultZoomMobile: (state) => Number(state.globalData.map_configuration.default_startup_zoom_mobile),
    siteSearchRange: (state) => Number(state.globalData.map_configuration.search_range),
    siteEverywhereService: (state) => state.globalData.everywhere_service,
    sitePrimaryPhoneNumber: (state) => state.globalData.primary_phone_number,
    siteEverywhereAddressLookup: (state) => state.globalData.everywhere_address_lookup,
    siteCurrencySymbol: (state) => state.globalData.currency.symbol,
    frontendDateFormat: (state) => state.globalData.time_and_date.frontend_date_format,
    siteCurrencySymbolPosition: (state) => state.globalData.currency_symbol_position.value,
    siteCurrencySymbolSpace: (state) => state.globalData.currency_symbol_space,
    siteCurrencyDecimal: (state) => state.globalData.currency_decimal,
    siteCurrentLocale: (state) => state.locale,
    siteCurrentLocaleDetail: (state) => state.localeDetail,
    siteAllLocales: (state) => state.locales,
    siteLangCode: (state) => state.langCode,
    sitehelpMenu: (state) => state.helpSubMenu,
    siteFaqTopicMenu: (state) => state.faqTopics,
    sitetestMenu: (state) => state.testMenu.map(translateMenuItem),
    siteServiceTypePageMenu: (state) => state.serviceTypePageMenu,
    siteMobileTestMenu: (state) => [
      {
        title: "<b>" + useTranslateStore("mainmenu.allTests") + "</b>",
        hasChild: false,
        href: "/tests",
        showAustralia: true,
      },
      ...state.testMenu.map(translateMenuItem),
    ],
    sitetestHealthSubMenu: (state) => state.testHealthSubMenu,
    siteMobileTestHealthSubMenu: (state) => [
      {
        title: "<b>Overview</b>",
        hasChild: false,
        href: "/tests/health-and-wellness",
      },
      ...state.testHealthSubMenu,
    ],
    sitehelpHealthSubMenu: (state) => state.helpHealthSubMenu,
    siteisAustralia: (state) => state.locale === "en-au",
    siteShowCustomCheckoutFieldsAustralia: (state) => state.globalData.show_custom_checkout_fields_australia,
    patientLoginLink: (state) => state.globalData.patient_login_link,
    siteBlogLanguage: (state) => ["en", "es"],
    siteClinicTimeSlotDuration: (state) => Number(state.globalData.clinic_time_slot_duration),
    siteClinicTimeFormat: (state) => state.globalData.clinic_time_format,
    siteClinicOpeningTimes: (state) => state.globalData.clinic_opening_times,
    siteEverywhereRegionLookUp: (state) => state.globalData.everywhere_region_lookup,
    siteClinicMapText: (state) => state.clinicMapText,
    sitetestSexualHealthSubMenu: (state) =>
      state.globalData.most_popular_tests.map((test) => {
        let category = "";
        if (test.primary_category) {
          let parent = "";
          if (test.primary_category.parent) {
            parent = test.primary_category.parent.slug + "/";
          }
          category = parent + test.primary_category.slug + "/";
        }

        return {
          title: test.title,
          description: test.summary,
          href: "/tests/" + category + test.slug,
          hasChild: false,
        };
      }),
    siteCountrySwitcherVisibility: (state) => state.countrySwitcherVisibility,
    landbotChatBox: (state) => state.landbotChat,
    siteNurseStationServiceEnabled: (state) => state.globalData.nurse_station,
    siteNurseStationOpeningTimes: (state) => state.globalData.nurse_station_opening_times,
  },
  // optional actions
  actions: {
    setClinicMapSearchText(data) {
      this.clinicMapText = data;
    },
    setCountrySwitcherVisibility(status) {
      this.countrySwitcherVisibility = status;
    },
    setLandbotChatBox(object) {
      this.landbotChat = object;
    },

    async resetHomeTestState() {
      try {
        const config = useRuntimeConfig();
        const locale = this.locale || this.defaultLocale || DEFAULT_SITE_LOCALE;
        const services = await $fetch(`/service-types`, {
          baseURL: `${config.public.apiBase}/${locale}`,
        });

        this.isHomeTestEnabled = services?.data?.some((s) => s.slug === "home-test-kit") ?? false;
      } catch (err) {
        console.error("[global] resetHomeTestState failed:", err);
        this.isHomeTestEnabled = false;
      }
    },

    async registerGlobalData(data) {
      this.locales = await getLocales(this.locale);
      const preferedCountry = useCookie("preferredCountry", {
        maxAge: 30 * 86400 * 2,
      });
      const preferredCookieLocale =
        preferedCountry.value && preferedCountry.value !== "undefined" ? preferedCountry.value : null;
      if (typeof data !== "string" || !data || data === "undefined") {
        data = preferredCookieLocale ?? this.defaultLocale;
      }
      if (data == "en") {
        this.locale = preferredCookieLocale ?? this.defaultLocale;
        this.langCode = data;
      } else if (data == "es") {
        if (preferredCookieLocale?.includes("es-")) {
          this.locale = preferredCookieLocale;
        } else {
          preferedCountry.value = this.defaultSpainishLocale;
          this.locale = this.defaultSpainishLocale;
        }
        this.langCode = data;
      } else {
        preferedCountry.value = data;
        this.locale = data;

        const localePrefix = data.split("-").at(0);
        this.langCode = localePrefix ?? this.langCode;
      }
      this.globalData = await useFetchConfiguration(this.locale);

      this.localeDetail = Object.values(this.locales).find((data) =>
        data.languages.find((langCode) => langCode.code == this.locale)
      );
      //if (this.localeDetail) {
      // this.langCode = this.localeDetail.languages.find(
      //   (data) => data.code == this.locale
      // )?.lang;
      //  }

      this.faqTopics = this.globalData.faq_categories.map((item) => ({
        title: item.title,
        href: `/faqs/${item.slug}`,
        slug: item.slug,
      }));

      if (this.globalData.help_and_advice_sexual_health_topics.length === 0) {
        const { data: topicData } = await useHelpAdviceSubmenu(this.locale);
        this.globalData.help_and_advice_sexual_health_topics = topicData.value.data;
      }

      const { data: serviceTypePageMenu } = await useServiceTypePageList(this.locale);
      this.serviceTypePageMenu = serviceTypePageMenu.value.data.map((item) => ({
        title: item.title,
        href: `/service-types/${item.slug}`,
      }));

      // Extract titles and generate URLs using generateHelpAdviceURL function
      this.helpSubMenu = this.globalData.help_and_advice_sexual_health_topics.map((item) => ({
        title: item.title,
        href: generateHelpAdviceURL(item),
      }));
      this.helpSubMenu = [...this.helpSubMenuOverview.map(translateMenuItem), ...this.helpSubMenu];

      if (this.siteSettings.categories.includes("health-and-wellness")) {
        /** Health & Wellness menu under tests **/
        if (this.globalData.health_and_wellness_categories.length === 0) {
          const subCategories = await useFetchSubCategoryLists("health-and-wellness", this.locale);
          this.globalData.health_and_wellness_categories = subCategories;
        }

        this.testHealthSubMenu = this.globalData.health_and_wellness_categories.map((item) => ({
          title: item.title,
          href: `/tests/health-and-wellness/${item.slug}`,
        }));

        /** Health & Wellness menu under Help & Advice **/
        if (this.globalData.help_and_advice_health_wellness_categories.length === 0) {
          const { data: helpsubCategories } = await useHelpAdviceSubCategories("health-and-wellness", this.locale);
          this.globalData.help_and_advice_health_wellness_categories = helpsubCategories.value.data;
        }

        this.helpHealthSubMenu = this.globalData.help_and_advice_health_wellness_categories.map((item) => ({
          title: item.title,
          href: `/help-and-advice/health-and-wellness/${item.slug}`,
        }));
      }
    },

    async getIpLookUp() {
      this.locales = await getLocales(this.locale);

      const dataWithoutExternalLink = Object.keys(this.locales)
        .map((key) => ({
          code: key,
          ...this.locales[key],
        }))
        .filter((country) => country.external_link == false);
      const geoLocationData = await useIpLookUp();
      const getLookUpLocale = geoLocationData?.value?.country_code;
      if (getLookUpLocale) {
        const getLocale = dataWithoutExternalLink.find((data) => data.code == getLookUpLocale);
        if (getLocale && getLocale.languages && getLocale.languages.length > 0) {
          return `${getLocale.primary_lang}-${getLookUpLocale.toLowerCase()}`;
        }
      }
      return this.defaultLocale;
    },

    async getPosition(position) {
      const { data: result } = await useReverseGeocodingWithGoogle(position.coords.latitude, position.coords.longitude);
      const results = result.value.results;
      if (results.length > 0) {
        const addressComponents = results[0].address_components;
        const component = addressComponents.find((component) => component.types.includes("country"));
        if (component) {
          const getCountry = Object.entries(this.locales)
            .map(([key, innerObj]) => {
              if (key == component.short_name) {
                return innerObj.languages;
              }
            })
            .find((data) => data);
          return getCountry[0].code;
        }
      }
    },
  },
});
